import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { ActionButton } from '@/shared/ui/action-button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { getConsignmentStatusBadge, type CancellationReason } from '@/entities/consignments';

import {
  getCancelationReasons,
  getNewStatusDescriptions,
  getStatusChangeTitles,
  type ConsignmentStatusChangeValues,
  type ConsignmentStatusTarget
} from '../model';

interface ConsignmentStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ConsignmentStatusTarget;
  isSubmitting?: boolean;
  onConfirm: (values: ConsignmentStatusChangeValues) => void;
}

/** Reason dialog of a status change, legacy `reqstatus.xhtml`. Its fields mount with the content, so every opening starts empty. */
export function ConsignmentStatusDialog({ open, onOpenChange, ...formProps }: ConsignmentStatusDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <ConsignmentStatusForm {...formProps} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

interface ConsignmentStatusFormProps extends Omit<ConsignmentStatusDialogProps, 'open' | 'onOpenChange'> {
  onClose: () => void;
}

function ConsignmentStatusForm({ target, isSubmitting = false, onConfirm, onClose }: ConsignmentStatusFormProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<CancellationReason>('A');
  const [comment, setComment] = useState('');

  const isCancellation = target.action === 'CANCEL';
  const newStatusDescription = target.statusCode ? getNewStatusDescriptions(t)[target.statusCode] : null;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{getStatusChangeTitles(t)[target.action]}</DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {target.statusCode && (
          <div className="space-y-2">
            <Label>{t('newcmr.reqstatus.new.status.label')}</Label>
            <Badge
              variant={getConsignmentStatusBadge(target.statusCode).variant}
              className={cn(
                'w-full justify-start whitespace-normal rounded-md px-4 py-3 text-sm',
                getConsignmentStatusBadge(target.statusCode).className
              )}>
              {newStatusDescription}
            </Badge>
          </div>
        )}

        {isCancellation && (
          <div className="space-y-2">
            <Label htmlFor="cancellationReason">{t('requestHistory.cancellationReason.label')}</Label>
            <Select value={reason} onValueChange={(value) => setReason(value as CancellationReason)}>
              <SelectTrigger id="cancellationReason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getCancelationReasons(t).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="statusComment">{t('newcmr.reqstatus.comment.label')}</Label>
          <Textarea
            id="statusComment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={t('requestHistory.remarks.desc')}
            maxLength={255}
            className="min-h-32"
          />
        </div>
      </div>

      <DialogFooter className="gap-2">
        <ActionButton type="button" intent="dismiss" onClick={onClose}>
          {t('gen.closedialog.label')}
        </ActionButton>
        <ActionButton
          intent="primary"
          type="button"
          onClick={() => onConfirm({ reason, comment })}
          disabled={isSubmitting}
          className="min-w-24">
          {t('newcmr.save.label')}
        </ActionButton>
      </DialogFooter>
    </>
  );
}
